const PAGES_SLUGS_URL = 'https://api.pricingsaas.com/functions/v1/api/pages/slugs';
const ANALYTICS_API_KEY = import.meta.env.VITE_ANALYTICS_API_KEY;

export interface PagesSlugsResponse {
  slugs: string[];
  total: number;
  metadata: {
    execution_time_ms: number;
    generated_at: string;
  };
}

/**
 * Fetches all page slugs from the analytics API.
 * Returns the list of slugs and total count.
 */
export async function fetchPagesSlugs(): Promise<PagesSlugsResponse> {
  // Check if API key is configured
  if (!ANALYTICS_API_KEY) {
    throw new Error('Analytics API key not configured. Please add VITE_ANALYTICS_API_KEY to your .env file.');
  }

  console.log('📡 [pagesAPI] Fetching pages slugs from:', PAGES_SLUGS_URL);

  try {
    const response = await fetch(PAGES_SLUGS_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANALYTICS_API_KEY}`,
      }
    });

    console.log('📡 [pagesAPI] Response status:', response.status);

    if (!response.ok) {
      let errorMessage = 'Failed to fetch pages slugs';
      try {
        const errorData = await response.json();
        console.error('📡 [pagesAPI] Error response:', errorData);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Analytics service is temporarily unavailable. Please try again later.');
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();

    // The API returns an array of slugs directly, not an object
    // Transform it into the expected format
    const slugs = Array.isArray(data) ? data : [];
    const total = slugs.length;

    console.log('📡 [pagesAPI] Parsed response:', { total, slugCount: slugs.length });

    return {
      slugs,
      total,
      metadata: {
        execution_time_ms: 0,
        generated_at: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('📡 [pagesAPI] Fetch error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to connect to analytics service. Please check your internet connection.');
  }
}
